import React from "react";
import { useCart } from "../contexts/CartContext";
import Counter from "./Counter";
import { OrderItem } from "../types/OrderItem";
import { useCatalog } from "../contexts/CatalogContext";

interface OrderItemsProps {
  orderItems?: OrderItem[];
  isCheckout?: boolean;
  isReturn?: boolean;
}

const OrderItems = ({
  orderItems,
  isCheckout = false,
  isReturn = false,
}: OrderItemsProps) => {
  const { cart, addToCart, removeFromCart } = useCart();
  const {
    getImagesByIds,
    colorOption,
    sizeOption,
    getOptionDataByColor,
    getOptionDataBySize,
  } = useCatalog();

  return (
    <ul
      // The checkout/return rows below are full-width receipt lines, so the
      // list can't shrink-wrap the way the old stacked layout did.
      className={`${
        isCheckout
          ? "w-full bg-gray-50 rounded-lg overflow-hidden"
          : isReturn
            ? "w-full"
            : ""
      } w-full`}
    >
      {(orderItems ?? cart).map((item, index) => {
        // Get Color and Size Values from Variation
        const color = item.variation.itemVariationData?.itemOptionValues?.find(
          (iov) => iov.itemOptionId?.includes(colorOption?.id ?? ""),
        )?.itemOptionValueId;
        const size = item.variation.itemVariationData?.itemOptionValues?.find(
          (iov) => iov.itemOptionId?.includes(sizeOption?.id ?? ""),
        )?.itemOptionValueId;

        // Get Image from Variation, falling back to the item's own images and
        // then any sibling variation's. Plenty of variations carry no imageIds
        // at all, which rendered a broken <img> showing the alt text.
        const imageUrl =
          getImagesByIds([
            item.variation.itemVariationData?.imageIds?.[0] ?? "",
          ])?.[0]?.imageData?.url ??
          getImagesByIds([
            ...(item.product.itemData?.imageIds ?? []),
            ...(item.product.itemData?.variations?.flatMap(
              (v) => v.itemVariationData?.imageIds ?? [],
            ) ?? []),
          ])?.[0]?.imageData?.url;

        const colorData = getOptionDataByColor(
          color ?? "",
        )?.itemOptionValueData;
        const sizeData = getOptionDataBySize(size ?? "")?.itemOptionValueData;

        // Square amounts are integer cents.
        const unitCents = Number(
          item.variation.itemVariationData?.priceMoney?.amount ?? 0,
        );
        const lineCents = unitCents * item.quantity;

        // Labeled, not bare values: "Crimson · M" reads as two anonymous
        // attributes, and the size — the one a customer actually double-checks
        // before paying — disappears into it. Unknown values are DROPPED rather
        // than printed as "N/A": a cap genuinely has no size, and "Size: N/A"
        // reads like something failed to load.
        const optionMeta = [
          colorData?.name && `Color: ${colorData.name}`,
          sizeData?.name && `Size: ${sizeData.name}`,
        ].filter(Boolean) as string[];
        // Fallback to the variation's own name (Square usually names it after
        // the options, e.g. "Stone / M") when the option lookup comes back
        // empty — a line with no variant at all leaves the customer unable to
        // check what they're about to pay for.
        const variationName = item.variation.itemVariationData?.name;
        const meta =
          optionMeta.length > 0
            ? optionMeta
            : variationName && variationName !== "Regular"
              ? [variationName]
              : [];

        // Checkout/return rows are read-only, so they get a proper receipt
        // layout: thumbnail, then name + variant meta, then the line total
        // right-aligned in its own column. The markup below keeps the counter
        // and is only used by the interactive cart.
        if (isCheckout || isReturn) {
          return (
            <li
              key={`${item.product.id}_${item.variation.id}`}
              className={`flex items-start gap-3 px-4 py-3 ${
                index > 0 ? "border-t border-gray-200" : ""
              }`}
            >
              {imageUrl ? (
                <img
                  src={imageUrl}
                  alt={item.product.itemData?.name ?? undefined}
                  // object-contain, not cover: product shots are centered on
                  // white and cover crops the design out of the thumbnail.
                  className="w-16 h-16 shrink-0 rounded-md bg-white object-contain border border-gray-200"
                />
              ) : (
                <div
                  aria-hidden="true"
                  className="w-16 h-16 shrink-0 rounded-md bg-gray-100 border border-gray-200"
                />
              )}
              <div className="min-w-0 flex-1">
                <h3 className="text-sm font-semibold leading-snug">
                  {item.product.itemData?.name}
                </h3>
                {meta.length > 0 && (
                  <p className="mt-0.5 text-xs text-gray-500">
                    {meta.join(" · ")}
                  </p>
                )}
                {/* The interactive <Counter> is the only other place quantity
                    appears and it's absent from these read-only rows, so spell
                    it out — otherwise a line for 2 reads exactly like a line
                    for 1 and the unit price can't be reconciled to the total. */}
                <p className="mt-1 text-xs text-gray-500">
                  Qty {item.quantity} × ${(unitCents / 100).toFixed(2)}
                </p>
              </div>
              <span className="shrink-0 text-sm font-semibold tabular-nums">
                ${(lineCents / 100).toFixed(2)}
              </span>
            </li>
          );
        }

        // Interactive cart row: thumbnail, then a text column whose first line
        // pairs the name with the line total and whose last line pairs the
        // stepper with the unit price. Both ends of the row share a baseline —
        // the stepper used to hang below an otherwise-empty right column.
        return (
          <li
            key={`${item.product.id}_${item.variation.id}`}
            className={`flex items-start gap-4 px-5 py-4 ${
              index > 0 ? "border-t border-accent-light" : ""
            }`}
          >
            {/* A missing src renders as a broken-image box sized by its alt
                text, which knocks the whole row out of alignment. Render a
                neutral tile instead when there's no URL. */}
            {imageUrl ? (
              <img
                src={imageUrl}
                alt={item.product.itemData?.name ?? undefined}
                // object-contain, not cover: product shots are centered on
                // white and cover crops the design out of the thumbnail.
                className="w-16 h-16 shrink-0 rounded-md border border-accent-light bg-white object-contain"
              />
            ) : (
              <div
                aria-hidden="true"
                className="w-16 h-16 shrink-0 rounded-md border border-accent-light bg-cream"
              />
            )}

            <div className="min-w-0 flex-1">
              <div className="flex items-start justify-between gap-3">
                <h3 className="min-w-0 text-sm font-semibold leading-snug text-brown-dark">
                  {item.product.itemData?.name}
                </h3>
                <span className="shrink-0 text-sm font-semibold leading-snug tabular-nums text-brown-dark">
                  ${(lineCents / 100).toFixed(2)}
                </span>
              </div>
              {/* Same dropped-when-unknown rule as the receipt rows above —
                  a cap shows only its colour rather than "Size: N/A". */}
              {meta.length > 0 && (
                <p className="mt-0.5 text-xs text-gray-500">
                  {meta.join(" · ")}
                </p>
              )}
              <div className="mt-2.5 flex items-center justify-between gap-3">
                <Counter
                  quantity={item.quantity}
                  add={() => addToCart(item, 1)}
                  remove={() => removeFromCart(item, 1)}
                  className="counter-container--sm"
                />
                <span className="shrink-0 text-xs text-gray-400 tabular-nums">
                  ${(unitCents / 100).toFixed(2)} each
                </span>
              </div>
            </div>
          </li>
        );
      })}
    </ul>
  );
};

export default OrderItems;
